import React, { useEffect, useRef, useState } from "react";
import { Headphones, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { useDemoBrand } from "@/lib/demoBrand";

const lineEndpoints = (slug, step) => ({
  save: `/admin/demos/${slug}/tour-lines/${step}`,
  reset: `/admin/demos/${slug}/tour-lines/${step}/reset`,
  preview: `/admin/demos/${slug}/tour-lines/${step}/preview`,
});

export default function TourLineEditor({ step, text, edited, onCancel, onSaved, autoFocus = true, endpoints, maxLength = 1500, maxSeconds }) {
  const { slug } = useDemoBrand();
  const ep = endpoints || lineEndpoints(slug, step);
  const [draft, setDraft] = useState(text);
  const [busy, setBusy] = useState(null); // "save" | "reset" | "preview" | null
  const [previewedText, setPreviewedText] = useState(null);
  const audioRef = useRef(null);
  const stopPreview = () => { try { audioRef.current?.pause(); } catch {} };
  useEffect(() => stopPreview, []);

  const preview = async () => {
    stopPreview();
    setBusy("preview");
    try {
      const { data } = await apiClient.post(ep.preview, { text: draft });
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      audioRef.current = new Audio(`${base}${data.url}`);
      audioRef.current.play().catch(() => {});
      setPreviewedText(draft.trim());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate preview");
    } finally {
      setBusy(null);
    }
  };

  const run = async (kind) => {
    stopPreview();
    setBusy(kind);
    try {
      const { data } = kind === "save"
        ? await apiClient.put(ep.save, { text: draft })
        : await apiClient.post(ep.reset);
      toast.success(kind === "save" ? "Narration re-recorded" : "Original wording restored");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-record narration");
    } finally {
      setBusy(null);
    }
  };

  const estSeconds = draft.trim().split(/\s+/).filter(Boolean).length / 2.5;
  const tooLong = maxSeconds && estSeconds > maxSeconds;
  const unchanged = draft.trim() === text.trim() || !draft.trim();

  return (
    <div data-testid="demo-tour-line-editor">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={5}
        autoFocus={autoFocus}
        maxLength={maxLength}
        disabled={!!busy}
        data-testid="demo-tour-line-textarea"
        className="w-full rounded-lg bg-navy-950/70 border border-white/10 focus:border-brand-cyan/60 outline-none p-3 text-sm text-slate-100 leading-relaxed resize-y"
      />
      <div className="mt-1 flex justify-between text-[10px] font-mono text-slate-500">
        <span>
          {draft.length}/{maxLength}
          {maxSeconds && <span className={tooLong ? "text-amber-300" : ""} data-testid="tour-line-duration-estimate"> · ~{estSeconds.toFixed(1)}s spoken</span>}
        </span>
        {previewedText !== null && previewedText === draft.trim() && (
          <span className="text-emerald-400" data-testid="demo-tour-line-preview-ready">Preview ready · save reuses it</span>
        )}
      </div>
      {tooLong && (
        <div data-testid="tour-line-too-long-warning" className="mt-1 text-[11px] text-amber-300">
          This line runs longer than the intro can stretch (12s max), so the end will be cut off. Try trimming it.
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        {edited && (
          <button
            onClick={() => run("reset")}
            disabled={!!busy}
            data-testid="demo-tour-line-reset"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-amber-500/10 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40"
          >
            {busy === "reset" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Reset
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={preview}
            disabled={!!busy || !draft.trim()}
            data-testid="demo-tour-line-preview"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-brand-cyan border border-brand-cyan/40 hover:bg-brand-cyan/10 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40"
          >
            {busy === "preview" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Headphones className="w-3 h-3" />} Preview
          </button>
          <button
            onClick={onCancel}
            disabled={!!busy}
            data-testid="demo-tour-line-cancel"
            className="px-2.5 py-1.5 rounded-lg text-slate-300 hover:bg-white/5 font-mono text-[10px] tracking-[0.12em] uppercase transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={() => run("save")}
            disabled={!!busy || unchanged}
            data-testid="demo-tour-line-save"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors disabled:opacity-40"
          >
            {busy === "save" && <Loader2 className="w-3 h-3 animate-spin" />}
            {busy === "save" ? "Recording…" : "Save & re-record"}
          </button>
        </div>
      </div>
    </div>
  );
}
